/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import allActions from "../Store/action";
import { useNavigate } from "react-router-dom";

const PDF_SESSION_KEY = "customer_onboarding_pdf_opened";

const CustomerOnboardingVideo = ({
  setStep,
  upcomingAppointments,
  isAppointmentBooked,
  license,
  questionnaireState,
  isVerify,
  taxpro_allow_appointments,
  isMessageSent,
  isPdfDownloaded,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const playerContainerRef = useRef(null);

  const [user, { profile }] = useSelector((state) => [
    state.user,
    state.dashboard,
  ]);

  const [isVideoWatched, setIsVideoWatched] = useState(false);
  const [isSafariPdfHandled, setIsSafariPdfHandled] = useState(false);

  const isSafariBrowser = useMemo(() => {
    const ua = window.navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(ua);
  }, []);

  useEffect(() => {
    const savedPdfState = sessionStorage.getItem(PDF_SESSION_KEY);
    if (savedPdfState === "true") {
      setIsSafariPdfHandled(true);
    }
  }, []);

  useEffect(() => {
    const syncPdfStateFromStorage = () => {
      const savedPdfState = sessionStorage.getItem(PDF_SESSION_KEY);
      if (savedPdfState === "true") {
        setIsSafariPdfHandled(true);
      }
    };

    window.addEventListener("focus", syncPdfStateFromStorage);
    document.addEventListener("visibilitychange", syncPdfStateFromStorage);

    return () => {
      window.removeEventListener("focus", syncPdfStateFromStorage);
      document.removeEventListener("visibilitychange", syncPdfStateFromStorage);
    };
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    const WISTIA_ID = "vrs93wkg91";

    script.src = "https://fast.wistia.com/assets/external/E-v1.js";
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      window._wq = window._wq || [];
      window._wq.push({
        id: WISTIA_ID,
        onReady: (video) => {
          video.bind("end", () => {
            const profileObj = {
              settings: {
                ...user?.LoginData?.settings,
                isOnboardingVideoSeen: true,
              },
            };

            dispatch(
              allActions.dashboard.updateProfile({
                uuid: user?.LoginData?.uuid,
                profileObj,
              })
            );

            setIsVideoWatched(true);
          });
        },
      });
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const pdfCompleted = !!isPdfDownloaded || !!isSafariPdfHandled;

  const handleNext = () => {
    if (user?.LoginData?.settings?.isPasswordChanged) {
      if (!isVerify) {
        setStep((prev) => prev + 2);
        return;
      }

      if (taxpro_allow_appointments && !isAppointmentBooked) {
        navigate(
          `?lead=${user?.LoginData?.lead?.[0]?.uuid}&customer=${user?.LoginData?.uuid}&operator=${user?.LoginData?.lead?.[0]?.userpro?.uuid}`,
          { replace: true }
        );
        setStep((prev) => prev + 3);
        return;
      }

      if (!license) {
        setStep((prev) => prev + 4);
        return;
      }

      if (questionnaireState?.value !== questionnaireState?.max) {
        setStep((prev) => prev + 5);
        return;
      }

      if (
        (!taxpro_allow_appointments || !!isAppointmentBooked) &&
        license &&
        questionnaireState?.value === questionnaireState?.max &&
        !isMessageSent
      ) {
        setStep((prev) => prev + 6);
        return;
      }

      if (
        (!taxpro_allow_appointments || !!isAppointmentBooked) &&
        license &&
        questionnaireState?.value === questionnaireState?.max &&
        isMessageSent &&
        !pdfCompleted
      ) {
        setStep((prev) => prev + 7);
        return;
      }

      if (
        (!taxpro_allow_appointments || !!isAppointmentBooked) &&
        license &&
        questionnaireState?.value === questionnaireState?.max &&
        isMessageSent &&
        pdfCompleted
      ) {
        setStep((prev) => prev + 8);
        return;
      }
    } else {
      setStep(2);
    }
  };

  useEffect(() => {
    if (profile?.state === true) {
      dispatch(allActions.user.ReGetUserData());

      if (isVideoWatched) {
        handleNext();
      }
    }
  }, [profile?.state]);

  const allDoneIncludingVideo =
    !!user?.LoginData?.settings?.isOnboardingVideoSeen &&
    !!user?.LoginData?.settings?.isPasswordChanged &&
    !!isVerify &&
    (!taxpro_allow_appointments || !!isAppointmentBooked) &&
    !!license &&
    questionnaireState?.value === questionnaireState?.max &&
    !!isMessageSent &&
    !!pdfCompleted;

  const onlyVideoLeft =
    !user?.LoginData?.settings?.isOnboardingVideoSeen &&
    !!user?.LoginData?.settings?.isPasswordChanged &&
    !!isVerify &&
    (!taxpro_allow_appointments || !!isAppointmentBooked) &&
    !!license &&
    questionnaireState?.value === questionnaireState?.max &&
    !!isMessageSent &&
    !!pdfCompleted;

  return (
    <div className="player-wrapper" ref={playerContainerRef}>
      <div
        className="wistia_responsive_padding"
        style={{ padding: "56.25% 0 0 0", position: "relative" }}
      >
        <div
          className="wistia_responsive_wrapper"
          style={{
            height: "100%",
            left: 0,
            position: "absolute",
            top: 0,
            width: "100%",
          }}
        >
          <iframe
            src="https://fast.wistia.net/embed/iframe/vrs93wkg91?seo=false&videoFoam=true"
            title="Wistia Video"
            allow="autoplay; fullscreen"
            allowTransparency="true"
            className="wistia_embed"
            name="wistia_embed"
            width="100%"
            height="100%"
            id="wistia_vrs93wkg91"
          />
        </div>

        <div
          className={`d-flex justify-content-${
            user?.LoginData?.settings?.newUser ? "between" : "center"
          } align-items-center mt-2 position-relative`}
        >
          {user?.LoginData?.settings?.newUser && !allDoneIncludingVideo && (
            <button
              className="btn btn-outline-primary px-4 cursor-pointer"
              onClick={() => setStep((prev) => prev - 1)}
            >
              ← Back
            </button>
          )}

          {!allDoneIncludingVideo && (
            <button
              disabled={onlyVideoLeft}
              className="btn btn-primary px-4 py-2 cursor-pointer"
              onClick={handleNext}
            >
              {onlyVideoLeft ? "Complete" : "Next Step →"}
            </button>
          )}
        </div>

        {isSafariBrowser && isSafariPdfHandled && !isPdfDownloaded && (
          <div className="text-success mt-2 text-center">
            PDF was opened successfully.
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerOnboardingVideo;